import {
  Col,
  FormFeedback,
  Input,
  Form,
  Label,
  Row,
  ModalBody,
  ModalHeader,
  Modal,
  Button,
} from "reactstrap";
import ConfirmActionModal from "components/common/ConfirmActionModal";
import { useFormik } from "formik";
import { toast } from "react-toastify";
import { useEffect, useMemo, useState } from "react";
import {
  WorkAssignmentSourceType,
  WorkAssignmentSourceTypeLabel,
} from "features/WorkAssignments/constants/work-assignment.constants";
import {
  createWorkAssignment,
  deleteWorkAssignment,
  getWorkAssignments,
  updateWorkAssignment,
} from "features/WorkAssignments/services/WorkAssignmentService";
import { WorkAssignment } from "features/WorkAssignments/types/work-assignment.types";
import { AsyncPaginatedSelect } from "components/common/AsyncPaginatedSelect";
import { getUserOptionsWithEmployeeProfile } from "features/Users/services/UserService";
import AppFlatpickr from "components/common/AppFlatpickr";
import moment from "moment";
import schema from "./schema";
import { DateOnly } from "components/common/Date";
import {
  type EmployeeOptionAttributes,
  EmployeeOption,
  EmployeeSingleValue,
} from "features/Employees/components/EmployeeSelectComponents";
import {
  defaultOptionsFromAssignments,
  userIdsFromAssignments,
} from "./mergeAssignments";
import { useProfile } from "features/Authentication/hooks/useProfile";
import { workAssignmentAssigneeId } from "features/WorkAssignments/utils/workAssignmentNormalize";

type SourceScheduleDetail = {
  key: string;
  label: string;
  value: string | null | undefined;
};

function scheduleDetailsForSource(
  sourceType: WorkAssignmentSourceType,
  source: WorkAssignment["source"] | undefined,
): SourceScheduleDetail[] {
  const s = source as Record<string, unknown> | undefined;
  if (!s) return [];

  if (sourceType === WorkAssignmentSourceType.PETTY_WORK) {
    return [
      {
        key: "plannedStartDate",
        label: "Planned start date",
        value: (s.plannedStartDate as string | null | undefined) ?? null,
      },
      {
        key: "plannedEndDate",
        label: "Planned end date",
        value: (s.plannedEndDate as string | null | undefined) ?? null,
      },
    ];
  }

  if (sourceType === WorkAssignmentSourceType.SITE_VISIT) {
    return [
      {
        key: "scheduledAt",
        label: "Scheduled date",
        value: (s.scheduledAt as string | null | undefined) ?? null,
      },
      {
        key: "deadlineAt",
        label: "Deadline",
        value: (s.deadlineAt as string | null | undefined) ?? null,
      },
    ];
  }

  return [];
}

function assigneeLabelFromAssignment(a: WorkAssignment): string {
  const u = a.user;
  if (u) {
    const name = [u.firstName, u.lastName].filter(Boolean).join(" ").trim();
    if (name) return name;
    if (u.email) return u.email;
  }
  return `User #${workAssignmentAssigneeId(a)}`;
}

const WorkAssignmentForm = ({
  show,
  sourceType: sourceTypeProp,
  sourceId: sourceIdProp,
  sourceLabel,
  isReassign = false,
  defaultPlannedStartAt,
  onCloseClick,
  onSuccess,
}: {
  show: boolean;
  sourceType: WorkAssignmentSourceType;
  sourceId?: number;
  sourceLabel?: string;
  isReassign?: boolean;
  defaultPlannedStartAt?: string | null;
  onCloseClick: () => void;
  onSuccess: () => void;
}) => {
  const { user: accessUser } = useProfile();
  const fromUserId =
    accessUser?.id != null &&
    Number.isFinite(Number(accessUser.id)) &&
    Number(accessUser.id) > 0
      ? Number(accessUser.id)
      : undefined;

  const sourceType = sourceTypeProp;
  const sourceId = sourceIdProp ?? 0;

  // Existing assignments (loaded on open for reassign mode)
  const [existingAssignments, setExistingAssignments] = useState<
    WorkAssignment[]
  >([]);
  const [loadingAssignments, setLoadingAssignments] = useState(false);

  // Reassign mode: the selected assignment being edited
  const [selectedAssignmentUserId, setSelectedAssignmentUserId] = useState<
    number | null
  >(null);
  const [selectedAssignment, setSelectedAssignment] =
    useState<WorkAssignment | null>(null);
  const [loadingSelectedAssignment, setLoadingSelectedAssignment] =
    useState(false);
  const [unassigning, setUnassigning] = useState(false);
  const [showUnassignConfirm, setShowUnassignConfirm] = useState(false);

  // Load existing assignments when opening in reassign mode
  useEffect(() => {
    if (!show || !isReassign || !sourceIdProp) {
      setExistingAssignments([]);
      setSelectedAssignmentUserId(null);
      setSelectedAssignment(null);
      return;
    }
    let cancelled = false;
    setLoadingAssignments(true);
    getWorkAssignments({
      sourceType: sourceTypeProp,
      sourceId: sourceIdProp,
      status: "ASSIGNED,IN_PROGRESS",
      limit: 20,
    })
      .then((res) => {
        if (cancelled) return;
        const rows: WorkAssignment[] = (res as any)?.data?.rows ?? [];
        setExistingAssignments(rows.filter((r) => r.isActive !== false));
      })
      .catch(() => {
        if (!cancelled) toast.error("Failed to load assignments.");
      })
      .finally(() => {
        if (!cancelled) setLoadingAssignments(false);
      });
    return () => {
      cancelled = true;
    };
  }, [show, isReassign, sourceTypeProp, sourceIdProp]);

  // When an existing assignee is chosen, fetch their specific assignment
  const handleAssigneeSelect = async (userId: number | null) => {
    setSelectedAssignmentUserId(userId);
    setSelectedAssignment(null);

    if (!userId) {
      validation.setFieldValue("plannedStartDate", defaultPlannedStartAt ?? "");
      validation.setFieldValue("plannedEndDate", "");
      validation.setFieldValue("role", "");
      validation.setFieldValue("remarks", "");
      validation.setFieldValue("userIds", []);
      return;
    }

    validation.setFieldValue("userIds", [userId]);

    setLoadingSelectedAssignment(true);
    try {
      const res = await getWorkAssignments({
        sourceType: sourceTypeProp,
        sourceId: sourceIdProp,
        toUserId: userId,
        status: "ASSIGNED,IN_PROGRESS",
        limit: 1,
      });
      const rows: WorkAssignment[] = (res as any)?.data?.rows ?? [];
      const assignment = rows[0] ?? null;
      setSelectedAssignment(assignment);
      if (assignment) {
        void validation.setFieldValue(
          "plannedStartDate",
          defaultPlannedStartAt ?? assignment.plannedStartDate ?? "",
        );
        void validation.setFieldValue(
          "plannedEndDate",
          assignment.plannedEndDate ?? "",
        );
        void validation.setFieldValue("role", assignment.role ?? "");
        void validation.setFieldValue("remarks", assignment.remarks ?? "");
      } else {
        void validation.setFieldValue(
          "plannedStartDate",
          defaultPlannedStartAt ?? "",
        );
        void validation.setFieldValue("plannedEndDate", "");
        void validation.setFieldValue("role", "");
        void validation.setFieldValue("remarks", "");
      }
    } catch {
      toast.error("Failed to load assignment details.");
    } finally {
      setLoadingSelectedAssignment(false);
    }
  };

  const handleUnassign = async () => {
    if (!selectedAssignment) return;
    setUnassigning(true);
    setShowUnassignConfirm(false);
    try {
      const response = await deleteWorkAssignment(selectedAssignment.id);
      if (response.success) {
        toast.success("Assignee removed.");
        onSuccess();
        handleClose();
      } else {
        toast.error((response as any).message ?? "Could not remove assignee.");
      }
    } catch {
      toast.error("Could not remove assignee.");
    } finally {
      setUnassigning(false);
    }
  };

  // Assign mode: defaults for multi-select
  const defaultEmployeeOptions = useMemo(
    () => defaultOptionsFromAssignments(existingAssignments),
    [existingAssignments],
  );
  const mergedUserIds = useMemo(
    () => userIdsFromAssignments(existingAssignments),
    [existingAssignments],
  );

  const sourceScheduleDetails = useMemo(
    () =>
      scheduleDetailsForSource(
        sourceType,
        selectedAssignment?.source ?? existingAssignments[0]?.source,
      ),
    [sourceType, selectedAssignment, existingAssignments],
  );

  const validation: any = useFormik({
    enableReinitialize: true,
    initialValues: {
      sourceType,
      sourceId,
      isReassign,
      plannedStartDate: defaultPlannedStartAt ?? "",
      plannedEndDate: "",
      userIds: isReassign ? [] : mergedUserIds.length > 0 ? mergedUserIds : [],
      remarks: "",
      role: "",
    },
    validationSchema: schema,
    onSubmit: async (values) => {
      try {
        const users = (values.userIds as number[]).map((userId) => ({
          userId,
          plannedStartDate: values.plannedStartDate || null,
          plannedEndDate: values.plannedEndDate || null,
          role: values.role || null,
          remarks: values.remarks || null,
        }));

        if (isReassign) {
          const response = await updateWorkAssignment(selectedAssignment!.id, {
            plannedStartDate: values.plannedStartDate || null,
            plannedEndDate: values.plannedEndDate || null,
            role: values.role || null,
            remarks: values.remarks || null,
          });

          if (response.success) {
            toast.success(response.message ?? "Assignment updated.");
            onSuccess();
            validation.resetForm();
            onCloseClick();
          } else {
            toast.error(
              (response as any).message ?? "Could not update assignment.",
            );
          }
        } else {
          const response = await createWorkAssignment({
            sourceType: sourceType as WorkAssignmentSourceType,
            sourceId: Number(sourceId),
            fromUserId,
            users,
          });
          if (response.success) {
            toast.success(response.message ?? "Assignment created.");
            onSuccess();
            validation.resetForm();
            onCloseClick();
          } else {
            toast.error(response.message ?? "Could not create assignment.");
          }
        }
      } catch (err) {
        toast.error(
          (err as any).response?.data?.message ?? "Something went wrong",
        );
      }
    },
  });

  const handleClose = () => {
    validation.resetForm();
    setExistingAssignments([]);
    setSelectedAssignmentUserId(null);
    setSelectedAssignment(null);
    setUnassigning(false);
    setShowUnassignConfirm(false);
    onCloseClick();
  };

  const typeLabel = WorkAssignmentSourceTypeLabel[sourceType] ?? sourceType;
  const modalTitle = isReassign
    ? `Edit assignment — ${sourceLabel ?? `${typeLabel} #${sourceId}`}`
    : `Assign user to ${sourceLabel ?? `${typeLabel} #${sourceId}`}`;

  const isLoading = loadingAssignments;
  const fieldsBusy = loadingSelectedAssignment;

  return (
    <>
      <Modal isOpen={show} toggle={handleClose}>
        <ModalHeader className="bg-primary-subtle p-3" toggle={handleClose}>
          {modalTitle}
        </ModalHeader>
        <ModalBody>
          {isLoading ? (
            <div className="text-center py-4">
              <span
                className="spinner-border spinner-border-sm me-1"
                role="status"
                aria-hidden="true"
              />
              Loading assignments…
            </div>
          ) : (
            <>
              {!isReassign && (
                <p className="text-muted small mb-3">
                  Assign a team member to this {typeLabel.toLowerCase()}. They
                  will see it in their work list from the start date.
                </p>
              )}
              {isReassign && (
                <p className="text-muted small mb-3">
                  Select an assigned user to view and edit their assignment
                  details.
                </p>
              )}
              <Form
                className="needs-validation"
                onSubmit={(e) => {
                  e.preventDefault();
                  validation.handleSubmit();
                  return false;
                }}
              >
                <Row>
                  {sourceScheduleDetails.length ? (
                    <Col xs={12}>
                      <div className="mb-3 p-3 rounded border bg-light-subtle">
                        <Label className="form-label mb-2">
                          Schedule details
                        </Label>
                        <Row className="g-2">
                          {sourceScheduleDetails.map((item) => (
                            <Col md={6} key={item.key}>
                              <p className="text-muted small mb-1">
                                {item.label}
                              </p>
                              <div className="fw-medium">
                                <DateOnly date={item.value ?? null} />
                              </div>
                            </Col>
                          ))}
                        </Row>
                      </div>
                    </Col>
                  ) : null}

                  {/* Reassign mode: single-select from existing assignees */}
                  {isReassign ? (
                    <Col xs={12}>
                      <div className="mb-3">
                        <Label className="form-label">Assigned user</Label>
                        {existingAssignments.length === 0 ? (
                          <p className="text-muted small">
                            No active assignments found for this{" "}
                            {typeLabel.toLowerCase()}.
                          </p>
                        ) : (
                          <Input
                            type="select"
                            className="form-select"
                            value={selectedAssignmentUserId ?? ""}
                            onChange={(e) => {
                              const val = e.target.value;
                              void handleAssigneeSelect(
                                val ? Number(val) : null,
                              );
                            }}
                          >
                            <option value="">— Select assignee —</option>
                            {existingAssignments.map((a) => {
                              const uid = workAssignmentAssigneeId(a);
                              return (
                                <option key={uid} value={uid}>
                                  {assigneeLabelFromAssignment(a)}
                                </option>
                              );
                            })}
                          </Input>
                        )}
                        {validation.touched.userIds &&
                        validation.errors.userIds ? (
                          <FormFeedback type="invalid" className="d-block">
                            {String(validation.errors.userIds)}
                          </FormFeedback>
                        ) : null}
                      </div>
                    </Col>
                  ) : (
                    /* Assign mode: multi-select any user */
                    <Col xs={12}>
                      <div className="mb-3">
                        <Label className="form-label">Assign to</Label>
                        <p className="text-muted small mb-1">
                          Search and select one or more users. Only users who
                          have an employee profile are listed.
                        </p>
                        <AsyncPaginatedSelect<EmployeeOptionAttributes>
                          key={`assign-users-${sourceType}-${sourceId}-c`}
                          isMulti
                          defaultOption={
                            defaultEmployeeOptions.length
                              ? defaultEmployeeOptions
                              : undefined
                          }
                          fetchOptions={getUserOptionsWithEmployeeProfile}
                          placeholder="Search users..."
                          value={validation.values.userIds ?? []}
                          onChange={(val: any) =>
                            validation.setFieldValue(
                              "userIds",
                              (val ?? []).map(
                                (o: EmployeeOptionAttributes) => o.value,
                              ),
                            )
                          }
                          onBlur={() =>
                            validation.setFieldTouched("userIds", true)
                          }
                          isInvalid={
                            validation.touched.userIds &&
                            !!validation.errors.userIds
                          }
                          components={{
                            Option: EmployeeOption,
                            SingleValue: EmployeeSingleValue,
                          }}
                        />
                        {validation.touched.userIds &&
                        validation.errors.userIds ? (
                          <FormFeedback type="invalid">
                            <div>{String(validation.errors.userIds)}</div>
                          </FormFeedback>
                        ) : null}
                      </div>
                    </Col>
                  )}

                  {/* Assignment detail fields — shown when a user is selected (reassign) or always (assign) */}
                  {(!isReassign || selectedAssignmentUserId != null) && (
                    <>
                      {fieldsBusy ? (
                        <Col xs={12}>
                          <div className="text-center py-3 text-muted small">
                            <span className="spinner-border spinner-border-sm me-1" />
                            Loading assignment details…
                          </div>
                        </Col>
                      ) : (
                        <>
                          <Col md={6}>
                            <div className="mb-3">
                              <Label className="form-label">
                                Planned start
                              </Label>
                              {defaultPlannedStartAt ? (
                                <Input
                                  type="text"
                                  readOnly
                                  disabled
                                  value={moment(defaultPlannedStartAt).format(
                                    "DD MMM, YYYY",
                                  )}
                                  className="form-control bg-light"
                                />
                              ) : (
                                <AppFlatpickr
                                  name="plannedStartDate"
                                  className={`form-control ${
                                    validation.errors.plannedStartDate &&
                                    validation.touched.plannedStartDate
                                      ? "is-invalid"
                                      : ""
                                  }`}
                                  placeholder="Select start date"
                                  options={{
                                    altInput: true,
                                    altFormat: "d M, Y",
                                    dateFormat: "d M, Y",
                                  }}
                                  onChange={(date: any) =>
                                    validation.setFieldValue(
                                      "plannedStartDate",
                                      moment(date[0]).format("DD MMMM, YYYY"),
                                    )
                                  }
                                  value={
                                    validation.values.plannedStartDate
                                      ? new Date(
                                          validation.values.plannedStartDate,
                                        )
                                      : ""
                                  }
                                />
                              )}
                              {validation.touched.plannedStartDate &&
                              validation.errors.plannedStartDate ? (
                                <FormFeedback type="invalid">
                                  {validation.errors.plannedStartDate}
                                </FormFeedback>
                              ) : null}
                            </div>
                          </Col>

                          <Col md={6}>
                            <div className="mb-3">
                              <Label className="form-label">
                                Planned end{" "}
                                <span className="text-muted fw-normal">
                                  (optional)
                                </span>
                              </Label>
                              <AppFlatpickr
                                name="plannedEndDate"
                                className={`form-control ${
                                  validation.errors.plannedEndDate &&
                                  validation.touched.plannedEndDate
                                    ? "is-invalid"
                                    : ""
                                }`}
                                placeholder="Select end date"
                                options={{
                                  altInput: true,
                                  altFormat: "d M, Y",
                                  dateFormat: "d M, Y",
                                }}
                                onChange={(date: any) =>
                                  validation.setFieldValue(
                                    "plannedEndDate",
                                    date?.[0]
                                      ? moment(date[0]).format("DD MMMM, YYYY")
                                      : "",
                                  )
                                }
                                value={
                                  validation.values.plannedEndDate
                                    ? new Date(validation.values.plannedEndDate)
                                    : ""
                                }
                              />
                              {validation.touched.plannedEndDate &&
                              validation.errors.plannedEndDate ? (
                                <FormFeedback type="invalid">
                                  {validation.errors.plannedEndDate}
                                </FormFeedback>
                              ) : null}
                            </div>
                          </Col>

                          <Col md={12}>
                            <div className="mb-3">
                              <Label className="form-label">
                                Role (optional)
                              </Label>
                              <Input
                                type="text"
                                className="form-control"
                                placeholder="e.g. Lead, Support"
                                name="role"
                                value={validation.values.role ?? ""}
                                onChange={validation.handleChange}
                                onBlur={validation.handleBlur}
                                invalid={
                                  validation.touched.role &&
                                  validation.errors.role
                                    ? true
                                    : false
                                }
                              />
                              {validation.touched.role &&
                              validation.errors.role ? (
                                <FormFeedback type="invalid">
                                  {validation.errors.role}
                                </FormFeedback>
                              ) : null}
                            </div>
                          </Col>

                          <Col md={12}>
                            <div className="mb-3">
                              <Label className="form-label">
                                Remarks (optional)
                              </Label>
                              <Input
                                type="textarea"
                                className="form-control"
                                rows="2"
                                placeholder="e.g. Lead for site visit"
                                name="remarks"
                                value={validation.values.remarks}
                                onChange={validation.handleChange}
                                onBlur={validation.handleBlur}
                                invalid={
                                  validation.touched.remarks &&
                                  validation.errors.remarks
                                    ? true
                                    : false
                                }
                              />
                              {validation.touched.remarks &&
                              validation.errors.remarks ? (
                                <FormFeedback type="invalid">
                                  <div>{validation.errors.remarks}</div>
                                </FormFeedback>
                              ) : null}
                            </div>
                          </Col>
                        </>
                      )}
                    </>
                  )}

                  <Col lg={12}>
                    <div className="hstack gap-2 justify-content-end mt-2">
                      <Button
                        type="button"
                        color="light"
                        onClick={handleClose}
                        disabled={validation.isSubmitting || unassigning}
                      >
                        Cancel
                      </Button>
                      {isReassign && selectedAssignment ? (
                        <Button
                          type="button"
                          color="danger"
                          outline
                          disabled={
                            unassigning || validation.isSubmitting || fieldsBusy
                          }
                          onClick={() => setShowUnassignConfirm(true)}
                        >
                          {unassigning ? (
                            <>
                              <span
                                className="spinner-border spinner-border-sm me-1"
                                role="status"
                                aria-hidden="true"
                              />
                              Removing…
                            </>
                          ) : (
                            <>
                              <i className="ri-user-unfollow-line align-bottom me-1" />
                              Unassign
                            </>
                          )}
                        </Button>
                      ) : null}
                      <Button
                        type="submit"
                        color="primary"
                        disabled={
                          validation.isSubmitting ||
                          fieldsBusy ||
                          unassigning ||
                          (isReassign && selectedAssignmentUserId == null)
                        }
                      >
                        {validation.isSubmitting ? (
                          <>
                            <span
                              className="spinner-border spinner-border-sm me-1"
                              role="status"
                              aria-hidden="true"
                            />
                            {isReassign ? "Saving…" : "Assigning…"}
                          </>
                        ) : isReassign ? (
                          "Save changes"
                        ) : (
                          "Assign"
                        )}
                      </Button>
                    </div>
                  </Col>
                </Row>
              </Form>
            </>
          )}
        </ModalBody>
      </Modal>

      <ConfirmActionModal
        show={showUnassignConfirm}
        iconClass="ri-user-unfollow-line"
        iconColor="text-danger"
        heading="Remove assignee?"
        description={`${selectedAssignment ? assigneeLabelFromAssignment(selectedAssignment) : "This user"} will be removed from this assignment. This cannot be undone.`}
        confirmText="Remove"
        confirmColor="danger"
        cancelText="Cancel"
        onConfirm={() => void handleUnassign()}
        onClose={() => setShowUnassignConfirm(false)}
      />
    </>
  );
};

export default WorkAssignmentForm;
